import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import { IMismatchSearchProps, IQasMismatchState, IResponseLogMismatchSearch, IResponseStatusMismatchSearch } from "@/types/qas/qasMismatchTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasMismatchState = {
	mismatchSearcherStart: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	mismatchSearcherStop: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	mismatchSearcherStatus: {
		requestStatus: RequestStatus.IDLE,
		error: "",
		responseStatus: "stopped",
		progress: 0,
	},
	mismatchSearcherLog: {
		status: RequestStatus.IDLE,
		mismatchesData: [],
		erorrData: [],
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// поиск несоответствий кэша ответов и документов
export const startMismatchSearch = createAsyncThunk(
	"qasMismatch/start",
	async ({ categoryId, modelGpt, promptTemplateId, promptAskTemplateId, answers }: IMismatchSearchProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_QAS}/${ENDPOINTS_QAS.MISMATCH_SEARCHER_START}`, {
				category: categoryId,
				model: modelGpt,
				template: promptTemplateId,
				asktemplate: promptAskTemplateId,
				answers,
			});
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// остановка поиска несоответствий кэша ответов и документов
export const stopMismatchSearch = createAsyncThunk("qasMismatch/stop", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.MISMATCH_SEARCHER_STOP}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// статус поиска несоответствий кэша ответов и документов
export const getStatusMismatchSearch = createAsyncThunk(
	"qasMismatch/status",
	async (_, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseStatusMismatchSearch> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.MISMATCH_SEARCHER_STATUS}`);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	},
	{
		condition: (_, { getState }) => {
			if ((getState() as RootState).qasMismatch.mismatchSearcherStatus.requestStatus === RequestStatus.LOADING) return false;
		},
	}
);

// логи поиска несоответствий кэша ответов и документов
export const getLogMismatchSearch = createAsyncThunk("qasMismatch/log", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseLogMismatchSearch> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.MISMATCH_SEARCHER_LOG}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const qasMismatchSlice = createSlice({
	name: "qasMismatch",
	initialState,
	reducers: {
		// изменнение ответа
		changeAnswer: (state, action: PayloadAction<{ table: "mismatchesData" | "erorrData"; idx: number; answer: string }>) => {
			if (action.payload.table === "mismatchesData") {
				state.mismatchSearcherLog.mismatchesData[action.payload.idx].description = action.payload.answer;
			} else {
				state.mismatchSearcherLog.erorrData[action.payload.idx].answer = action.payload.answer;
			}
		},
		// удаление строк в таблице поиска несоответствий
		deleteRowsInMismatchTable: (state, action: PayloadAction<{ table: "answers" | "errors"; indices: number[] }>) => {
			if (state.mismatchSearcherLog.mismatchesData.length > 0 && action.payload.table === "answers") {
				state.mismatchSearcherLog.mismatchesData = state.mismatchSearcherLog.mismatchesData.filter((_item, idx) => !action.payload.indices.includes(idx));
			} else if (state.mismatchSearcherLog.erorrData.length > 0 && action.payload.table === "errors") {
				state.mismatchSearcherLog.erorrData = state.mismatchSearcherLog.erorrData.filter((_item, idx) => !action.payload.indices.includes(idx));
			}
		},
		// очистка state
		clearState: (state) => {
			state.mismatchSearcherStart = initialState.mismatchSearcherStart;
			state.mismatchSearcherStop = initialState.mismatchSearcherStop;
			state.mismatchSearcherStatus = initialState.mismatchSearcherStatus;
			state.mismatchSearcherLog = initialState.mismatchSearcherLog;
		},
		// очистка статуса старта поиска несоответствий кэша ответов и документов
		clearMismatchSearcherStart: (state) => {
			state.mismatchSearcherStart = initialState.mismatchSearcherStart;
		},
		// очистка статуса остановки поиска несоответствий кэша ответов и документов
		clearMismatchSearcherStop: (state) => {
			state.mismatchSearcherStop = initialState.mismatchSearcherStop;
		},
		// очистка статуса процесса поиска несоответствий кэша ответов и документов
		clearMismatchSearcherStatus: (state) => {
			state.mismatchSearcherStatus = initialState.mismatchSearcherStatus;
		},
		// очистка логов поиска несоответствий кэша ответов и документов
		clearMismatchSearcherLog: (state) => {
			state.mismatchSearcherLog = initialState.mismatchSearcherLog;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(startMismatchSearch.pending, (state) => {
				state.mismatchSearcherStart.status = RequestStatus.LOADING;
			})
			.addCase(startMismatchSearch.fulfilled, (state, action) => {
				if (action.payload) {
					state.mismatchSearcherStart.status = RequestStatus.IDLE;
					state.mismatchSearcherStart.error = action.payload.error;
					state.mismatchSearcherStart.message = action.payload.message;
				} else state.mismatchSearcherStart.status = RequestStatus.FAILED;
			})
			.addCase(startMismatchSearch.rejected, (state, action: PayloadAction<unknown>) => {
				state.mismatchSearcherStart.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.mismatchSearcherStart.error = error;
				state.mismatchSearcherStart.message = message;
			})
			.addCase(stopMismatchSearch.pending, (state) => {
				state.mismatchSearcherStop.status = RequestStatus.LOADING;
			})
			.addCase(stopMismatchSearch.fulfilled, (state, action) => {
				if (action.payload) {
					state.mismatchSearcherStop.status = RequestStatus.IDLE;
					state.mismatchSearcherStop.error = action.payload.error;
					state.mismatchSearcherStop.message = action.payload.message;
				} else state.mismatchSearcherStop.status = RequestStatus.FAILED;
			})
			.addCase(stopMismatchSearch.rejected, (state, action: PayloadAction<unknown>) => {
				state.mismatchSearcherStop.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.mismatchSearcherStop.error = error;
				state.mismatchSearcherStop.message = message;
			})
			.addCase(getStatusMismatchSearch.pending, (state) => {
				state.mismatchSearcherStatus.requestStatus = RequestStatus.LOADING;
			})
			.addCase(getStatusMismatchSearch.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.mismatchSearcherStatus.requestStatus = RequestStatus.IDLE;
					state.mismatchSearcherStatus.error = action.payload.error;
					state.mismatchSearcherStatus.responseStatus = action.payload.status;
					state.mismatchSearcherStatus.progress = action.payload.progress;
				} else state.mismatchSearcherStatus.requestStatus = RequestStatus.FAILED;
			})
			.addCase(getStatusMismatchSearch.rejected, (state, action: PayloadAction<unknown>) => {
				state.mismatchSearcherStatus.requestStatus = RequestStatus.FAILED;
				const { error } = getResponseError(action);
				state.mismatchSearcherStatus.error = error.toString();
			})
			.addCase(getLogMismatchSearch.pending, (state) => {
				state.mismatchSearcherLog.status = RequestStatus.LOADING;
			})
			.addCase(getLogMismatchSearch.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "mismatches" in action.payload && Array.isArray(action.payload.mismatches)) {
					state.mismatchSearcherLog.status = RequestStatus.IDLE;
					state.mismatchSearcherLog.mismatchesData = action.payload.mismatches.filter(({ description }) => description !== "");
					state.mismatchSearcherLog.erorrData = action.payload.mismatches.filter(({ error }) => error !== "");
				} else state.mismatchSearcherLog.status = RequestStatus.FAILED;
			})
			.addCase(getLogMismatchSearch.rejected, (state, action: PayloadAction<unknown>) => {
				state.mismatchSearcherLog.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.mismatchSearcherLog.error = error;
				state.mismatchSearcherLog.message = message;
			});
	},
});

export const {
	changeAnswer,
	deleteRowsInMismatchTable,
	clearState,
	clearMismatchSearcherStart,
	clearMismatchSearcherStop,
	clearMismatchSearcherStatus,
	clearMismatchSearcherLog,
} = qasMismatchSlice.actions;

export const selectMismatchSearcherStart = (state: RootState) => state.qasMismatch.mismatchSearcherStart;
export const selectMismatchSearcherStop = (state: RootState) => state.qasMismatch.mismatchSearcherStop;
export const selectMismatchSearcherStatus = (state: RootState) => state.qasMismatch.mismatchSearcherStatus;
export const selectMismatchSearcherLog = (state: RootState) => state.qasMismatch.mismatchSearcherLog;

export default qasMismatchSlice.reducer;
